import React, { useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getPostLatestDates } from "../../app/api/postLatestDates";
import PostLatestDatesPanel from "../../components/TrendPostCount/PostLatestDatesPanel";
import { IPostLatestDatesResult } from "../../interface/IPostLatestDates";

type Props = {
  isSidebarOpen: boolean;
};

const PostLatestDates: React.FC<Props> = ({ isSidebarOpen }) => {
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [data, setData] = useState<IPostLatestDatesResult | null>(null);

  const handleLoad = async () => {
    setLoading(true);
    setHasFetched(true);

    try {
      const result = await getPostLatestDates();
      setData(result);
    } catch (error) {
      setData(null);
      const status = (error as AxiosError).response?.status;
      if (status === 403) {
        toast.error("You do not have access to this feature", {
          hideProgressBar: true,
        });
      } else if (status !== 400) {
        toast.error("Failed to fetch latest post dates", {
          hideProgressBar: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} fixed transition-all duration-500 ease-in-out overflow-y-scroll h-[calc(100vh-80px)] ml-1 px-6 py-5`}
    >
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-primary">Latest Post</h1>
          <p className="text-sm text-secondary mt-1">
            Find the most recently stored post and the post with the latest
            published date in the posts table.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-light shadow-sm mb-6 p-5 flex justify-end">
          <button
            type="button"
            onClick={handleLoad}
            disabled={loading}
            className="h-11 px-8 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Loading..." : "Load"}
          </button>
        </div>

        {loading && <Skeleton height={200} borderRadius={16} />}

        {!loading && !hasFetched && (
          <div className="bg-white rounded-2xl border border-light p-8 text-center shadow-sm">
            <p className="text-primary font-medium">No results yet</p>
            <p className="text-sm text-secondary mt-2">
              Click Load to fetch the latest post dates.
            </p>
          </div>
        )}

        {!loading && hasFetched && <PostLatestDatesPanel data={data} />}
      </div>
    </div>
  );
};

export default PostLatestDates;
